import { useNavigate } from 'react-router';
import { useEffect, useMemo } from 'react';
import {
  useBankIDSessionsConfirmMutation,
  useBankIDSessionsQuery,
  useBankIDSessionsRejectMutation,
} from 'hooks/webapi';
import {
  BANK_ID_ACCOUNT_SELECTION,
  BANK_ID_ERROR,
  MANUAL_OCR,
  PSD2_BANK_ACCOUNT_SELECT,
} from 'Router/routes';
import { format } from 'utils/format';
import { formatFullName } from 'utils/name';
import { formatBankIdAddress } from 'utils/address';
import { handleErrorRedirect } from 'pages/Registration/BankId/BankInfo/useBankIdInfo';

export default function useBankIdConfirm() {
  const sessionId = localStorage.getItem('bankIdSessionId');
  const navigate = useNavigate();

  const { data: bankIdSessionData, isLoading: bankIdSessionIsLoading } =
    useBankIDSessionsQuery({ uuid: sessionId! }, Boolean(sessionId));
  const { mutateAsync: rejectBankId, isPending: rejectedBankIdIsPending } =
    useBankIDSessionsRejectMutation();
  const { mutateAsync: confirmBankId, isPending: confirmBankIdIsPending } =
    useBankIDSessionsConfirmMutation();

  const handleConfirmBankID = async () => {
    if (!bankIdSessionData) {
      return;
    }

    if (!sessionId) {
      navigate(BANK_ID_ERROR, { state: { error: 'session_id_not_loaded' } });
      return;
    }

    await confirmBankId({
      uuid: sessionId,
    });

    // If the user has payment accounts, navigate to the account selection page
    if (bankIdSessionData.paymentAccounts) {
      navigate(BANK_ID_ACCOUNT_SELECTION);
      return;
    }

    // If the user has no payment accounts, navigate to the PSD2 bank account selection page
    // TODO: Make sure this is what we want to do (maybe we should navigate to identification status page instead)
    navigate(PSD2_BANK_ACCOUNT_SELECT);
  };

  const handleRejectedBankID = async () => {
    if (!sessionId) {
      handleErrorRedirect(navigate, 'session_id_not_loaded');
      return;
    }

    await rejectBankId(sessionId);
    navigate(MANUAL_OCR);
  };

  // Returns null for non-SUCCESS sessions so the UI renders nothing until data is confirmed valid
  const info = useMemo(() => {
    if (!bankIdSessionData) {
      return null;
    }

    if (bankIdSessionData?.sessionStatus !== 'SUCCESS') {
      return null;
    }

    const {
      givenName,
      familyName,
      age,
      birthdate,
      birthnumber,
      identificationDocument,
      addresses,
    } = bankIdSessionData;

    // Extract the permanent residence address if available
    const permanentResidence = addresses.find(
      (address) => address.type === 'PERMANENT_RESIDENCE',
    );

    const fullAddress = permanentResidence
      ? formatBankIdAddress(permanentResidence)
      : '';

    // Prepare the response object with user details
    return {
      fullName: formatFullName({ firstName: givenName, lastName: familyName }),
      fullAge: `${age} / ${format.dateTime(birthdate, 'date')}`,
      birthnumber: birthnumber ?? '',
      cardNumber: identificationDocument?.number ?? '',
      fullAddress,
      typeOfCard: identificationDocument?.type ?? '',
    };
  }, [bankIdSessionData]);

  useEffect(() => {
    const isError = new URLSearchParams(window.location.search).get('error');

    const isSessionStatusError = !(
      bankIdSessionData?.sessionStatus === 'SUCCESS' ||
      bankIdSessionData?.sessionStatus === undefined
    );

    if (isError) {
      // save to handle on BankError screen
      handleErrorRedirect(navigate, isError, true);
      return;
    }

    if (isSessionStatusError) {
      // save to handle on BankError screen
      handleErrorRedirect(navigate, bankIdSessionData?.sessionStatus, true);
      return;
    }
  }, [navigate, bankIdSessionData?.sessionStatus]);

  return {
    isLoading: bankIdSessionIsLoading,
    isPending: confirmBankIdIsPending || rejectedBankIdIsPending,
    handleConfirmBankID,
    handleRejectedBankID,
    info,
  };
}
